// src/components/ShareButton.tsx
import FacebookIcon from '@mui/icons-material/Facebook';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { SvgIcon } from '@mui/material';
import { Button, Popover, Box, Typography, IconButton, Snackbar, Alert } from '@mui/material';
import React, { useState } from 'react';
import { trackSocialShare, trackShareButtonClick } from './GoogleAnalytics';

// Custom X (Twitter) Icon component
const XIcon: React.FC<{ fontSize?: 'small' | 'medium' | 'large' }> = ({ fontSize = 'medium' }) => {
  return (
    <SvgIcon fontSize={fontSize}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </SvgIcon>
  );
};

interface ShareButtonProps {
  billTitle?: string; // Title of the bill to share (for backward compatibility)
  title?: string; // Generic title for any content type
  stateName?: string | null; // Name of the state for the bill
  customShareText?: string; // Custom share text override
  contentType?: 'bill' | 'article'; // Type of content being shared
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

const ShareButton: React.FC<ShareButtonProps> = ({
  billTitle,
  title,
  stateName,
  customShareText,
  contentType = 'bill',
  size = 'medium',
  variant = 'outlined',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const shareUrl = window.location.href;
  const stateText = stateName || 'your state';

  // Use title prop or fall back to billTitle for backward compatibility
  const displayTitle = title || billTitle || '';

  // Generate share text based on content type or use custom text
  const getShareText = (): string => {
    if (customShareText) return customShareText;

    if (contentType === 'article') {
      return `"${displayTitle}" - Read this important article about family court reform on RobertGarza.us and learn how you can help create change in your state.`;
    }

    // Default bill share text
    return `Check out ${stateText}'s ${displayTitle} bill proposal and send it to your local representative. It will fix one of the many problems in your local family court when passed into law.`;
  };

  const shareText = getShareText();

  // Detect if user is on mobile device
  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform))
    );
  };

  // Check if native sharing is available and should be used
  const shouldUseNativeShare = () => {
    if (!isMobileDevice() || !navigator.share) return false;

    try {
      // Some browsers have canShare, others don't
      if (navigator.canShare) {
        return navigator.canShare({ title: displayTitle, text: shareText, url: shareUrl });
      }
      // If canShare doesn't exist, assume share is available on mobile
      return true;
    } catch {
      return false;
    }
  };

  // Handle primary share action
  const handleShare = async () => {
    // Track share button click
    trackShareButtonClick(displayTitle, stateName || undefined);

    // On mobile with native share support, use native sharing
    if (shouldUseNativeShare()) {
      try {
        await navigator.share({
          title: contentType === 'article' ? displayTitle : `${displayTitle} - ${stateText}`,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // Fall through to desktop behavior
      }
    }

    // On desktop or when native share fails, show popover
    setAnchorEl(document.activeElement as HTMLElement);
  };

  // Optimized copy to clipboard for desktop
  const handleCopyLink = async () => {
    try {
      const fullShareText = `${shareText}\n\n${shareUrl}`;
      await navigator.clipboard.writeText(fullShareText);
      trackSocialShare('Copy Link', displayTitle);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
      handleClose();
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = `${shareText}\n\n${shareUrl}`;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
        handleClose();
      } catch (fallbackErr) {
        alert('Unable to copy to clipboard. Please copy the URL manually.');
      }
    }
  };

  // Social media share links - Updated with working formats
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

  // Facebook - Current working URL format
  const facebookUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  // LinkedIn - Use the working sharing format
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<ShareIcon />}
        onClick={handleShare}
        sx={{
          borderColor: variant === 'outlined' ? 'primary.main' : undefined,
          color: 'primary.main',
          fontWeight: 600,
          minWidth: { xs: 120, sm: 140 },
          '&:hover': {
            bgcolor: variant === 'text' ? 'primary.light' : 'primary.main',
            color: variant === 'text' ? 'primary.dark' : 'white',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease-in-out',
        }}
      >
        Share
      </Button>

      {/* Desktop-optimized sharing popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 4,
            minWidth: { xs: 320, sm: 380 },
            maxWidth: 480,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Share this {contentType === 'article' ? 'article' : 'bill'}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
              lineHeight: 1.5,
              fontSize: '0.875rem',
            }}
          >
            {shareText}
          </Typography>

          {/* Quick copy section for desktop */}
          <Box sx={{ mb: 3 }}>
            <Button
              onClick={handleCopyLink}
              variant="contained"
              startIcon={<ContentCopyIcon />}
              fullWidth
              sx={{
                mb: 1,
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' },
              }}
            >
              Copy Message & Link
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center' }}>
              Perfect for texting or messaging
            </Typography>
          </Box>

          {/* Social media sharing options */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Or share directly to:
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <IconButton
              href={xUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on X (Twitter)"
              onClick={() => trackSocialShare('X', displayTitle)}
              sx={{
                bgcolor: '#000000',
                color: 'white',
                width: 48,
                height: 48,
                borderRadius: '50%',
                '&:hover': { bgcolor: '#333333', transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon fontSize="small" />
            </IconButton>

            <IconButton
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on Facebook"
              onClick={() => trackSocialShare('Facebook', displayTitle)}
              sx={{
                bgcolor: '#4267b2',
                color: 'white',
                width: 48,
                height: 48,
                borderRadius: '50%',
                '&:hover': { bgcolor: '#365899', transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              <FacebookIcon fontSize="small" />
            </IconButton>

            <IconButton
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
              onClick={() => trackSocialShare('LinkedIn', displayTitle)}
              sx={{
                bgcolor: '#0077b5',
                color: 'white',
                width: 48,
                height: 48,
                borderRadius: '50%',
                '&:hover': { bgcolor: '#006399', transform: 'scale(1.05)' },
                transition: 'all 0.2s ease',
              }}
            >
              <LinkedInIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Popover>

      {/* Success notification */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          Share message copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareButton;
